import { marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({
  gfm: true,
  breaks: true,
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A' && node.hasAttribute('href')) {
    const href = node.getAttribute('href') || '';
    if (/^https?:\/\//i.test(href)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }

  if (node.nodeName === 'IMG') {
    node.setAttribute('loading', 'lazy');
    node.setAttribute('decoding', 'async');
    if (!node.hasAttribute('alt')) {
      node.setAttribute('alt', '');
    }
  }
});

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'b',
    'i',
    's',
    'del',
    'ul',
    'ol',
    'li',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'caption',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'pre',
    'a',
    'hr',
    'img',
    'span',
    'div',
  ],
  ALLOWED_ATTR: [
    'href',
    'target',
    'rel',
    'src',
    'alt',
    'title',
    'class',
    'loading',
    'decoding',
  ],
  ADD_DATA_URI_TAGS: ['img'],
};

const CITATION_PATTERN = /【([^】\n]+)】/g;

function fileToMarkdown(file) {
  const isImage =
    typeof file.mediaType === 'string' && file.mediaType.startsWith('image/');
  const name = file.filename || (isImage ? 'Bild' : 'Datei');
  const safeUrl = String(file.url).replace(/[)\s]/g, encodeURIComponent);

  if (isImage) {
    return `\n\n![${name}](${safeUrl})\n\n`;
  }

  return `\n\n[${name}](${safeUrl})\n\n`;
}

function insertPositionedFiles(text, files) {
  const positionedFiles = files
    .map((file, index) => ({ file, index }))
    .filter(({ file }) => Number.isFinite(file.insertAt));

  if (positionedFiles.length === 0) return text;

  return positionedFiles
    .sort((a, b) => {
      if (b.file.insertAt !== a.file.insertAt) {
        return b.file.insertAt - a.file.insertAt;
      }
      return b.index - a.index;
    })
    .reduce((result, { file }) => {
      const position = Math.max(0, Math.min(result.length, file.insertAt));
      return `${result.slice(0, position)}${fileToMarkdown(file)}${result.slice(
        position,
      )}`;
    }, text);
}

export function formatBotContent(text) {
  if (typeof text !== 'string' || text === '') return '';
  return DOMPurify.sanitize(marked.parse(text), SANITIZE_CONFIG);
}

export function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function extractMessageText(message) {
  if (!message || typeof message !== 'object') return '';
  if (typeof message.content === 'string') return message.content;
  if (Array.isArray(message.parts)) {
    return message.parts
      .map((part) =>
        part && part.type === 'text' && typeof part.text === 'string'
          ? part.text
          : '',
      )
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

export function renderAssistantContent(text, sources = new Map(), files = []) {
  let processed = typeof text === 'string' ? text : '';
  const safeFiles = Array.isArray(files) ? files : [];

  processed = insertPositionedFiles(processed, safeFiles);

  if (sources.size > 0) {
    processed = processed.replace(CITATION_PATTERN, (_match, id) => {
      const source = sources.get(String(id).trim());
      if (source && source.url) {
        const label = source.title || 'Source';
        return ` [${label}](${source.url})`;
      }
      return '';
    });
  } else {
    processed = processed.replace(CITATION_PATTERN, '');
  }

  if (safeFiles.length > 0) {
    processed += safeFiles
      .filter((file) => !Number.isFinite(file.insertAt))
      .map((file) => fileToMarkdown(file))
      .join('');
  }

  return formatBotContent(processed);
}

export function detectPdfUrl(content) {
  if (typeof content !== 'string') return null;
  const pdfMatch = content.match(/https?:\/\/[^\s)]+\.pdf/i);
  if (!pdfMatch) return null;

  const parts = pdfMatch[0].split('/');
  return {
    url: pdfMatch[0],
    filename: parts[parts.length - 1] || 'brandmap.pdf',
  };
}
